/**
 * Resident Detail Dialog Component
 *
 * Modal chrome only — backdrop, centering, ESC/click-outside dismissal, and the
 * Close footer. The detail itself lives in {@link ResidentDetailContent}, which
 * is shared with the embeddable resident-detail widget so Care Queue and My
 * Navigator render exactly the same clinical sections.
 */

import React from "react";
import { ResidentDetailContent } from "./ResidentDetailContent";
import { ResidentSummary } from "../../types/residents";

interface ResidentDetailDialogProps {
  resident: ResidentSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialInsightsDays?: number;
  disabledRiskFactors?: string[];
  aiSummaryEnabled?: boolean;
  aiReasoningVisible?: boolean;
}

export const ResidentDetailDialog: React.FC<ResidentDetailDialogProps> = ({
  resident,
  open,
  onOpenChange,
  initialInsightsDays,
  disabledRiskFactors = [],
  aiSummaryEnabled = false,
  aiReasoningVisible = false,
}) => {
  // Handle ESC key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open, onOpenChange]);

  if (!open || !resident) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={() => onOpenChange(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal */}
      <div
        className="relative z-10 flex flex-col bg-white rounded-lg shadow-xl w-full h-[calc(100vh-80px)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <ResidentDetailContent
          resident={resident}
          variant="modal"
          initialInsightsDays={initialInsightsDays}
          disabledRiskFactors={disabledRiskFactors}
          aiSummaryEnabled={aiSummaryEnabled}
          aiReasoningVisible={aiReasoningVisible}
          onClose={() => onOpenChange(false)}
        />

        {/* Footer */}
        <div className="flex-shrink-0 px-4 py-2 border-t border-gray-200 flex justify-end">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-1.5 bg-[#228189] text-white font-medium rounded hover:bg-[#1a6670] focus:outline-none focus:ring-2 focus:ring-[#228189] focus:ring-offset-2 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
